import React, { useState, useRef, useEffect } from "react";
import {
  Search,
  X,
  SlidersHorizontal,
  ChevronRight,
  ChevronDown,
  Check,
  Calendar,
  ArrowLeft,
  RotateCcw,
  Columns3,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  PAST_DATE_PRESETS,
  FUTURE_DATE_PRESETS,
  dateFilterLabel,
  isFilterActiveValue,
  type FilterOption,
} from "@/lib/filters";
import { FieldFilterChips, type PickerItem } from "./AddFilter";
import { AddQuickFilter, EditQuickFilters, QuickFieldPill, fieldEntry, fieldKeyOf, isFieldEntry } from "./QuickFilters";
import { useQuickFilters } from "@/lib/useQuickFilters";
import { SortMenu } from "./SortMenu";
import type { FieldFilterValue, FilterFieldDef } from "@/lib/useFieldFilters";

export interface FilterField {
  name: string;
  label: string;
  icon?: React.ElementType;
  /** "select" (options) or "date" (presets + custom range) */
  type: "select" | "date";
  options?: FilterOption[];
  /** For date fields: past (created on) or future (close / due) presets */
  dates?: "past" | "future";
  defaultValue: string;
  /** Hide the field (e.g. owner filter for someone who only sees their own records) */
  hidden?: boolean;
}

interface FilterBarProps {
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    onEnter?: () => void;
  };
  fields: FilterField[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  onReset: () => void;
  /** Rows after filtering / before filtering, and what they are ("leads") */
  shown?: number;
  total?: number;
  noun?: string;
  /** Page-specific controls shown at the end of the first row (status buttons, view toggle…) */
  children?: React.ReactNode;
  /** Lets the person choose which table columns to show */
  columns?: { options: ColumnOption[]; value: string[]; onChange: (keys: string[]) => void };
  /** "+ Add filter" on any field the API offers (chips after the fixed pills) */
  fieldFilters?: { fields: FilterFieldDef[]; value: FieldFilterValue[]; onChange: (next: FieldFilterValue[]) => void; suggestUrl?: string };
  /** "Sort by" menu: value is "" (the page's own order), "field" or "-field" */
  sort?: { fields: FilterFieldDef[]; value: string; onChange: (value: string) => void; defaultLabel?: string };
  /** Lets each person choose which quick filters sit on the row; remembered per browser under this name ("leads") */
  quickKey?: string;
  className?: string;
}

/** A column a list can show or hide; locked ones (the record's name, its actions) are always on */
export interface ColumnOption {
  key: string;
  label: string;
  locked?: boolean;
}

/**
 * FilterBar: a search box, one pill per filter (click a pill to choose; a filled pill is active and has an ×),
 * the page's own controls, an optional Columns menu, and the result count.
 */
export function FilterBar({
  search,
  fields,
  values,
  onChange,
  onReset,
  shown,
  total,
  noun = "records",
  children,
  columns,
  fieldFilters,
  sort,
  quickKey,
  className,
}: FilterBarProps) {
  const visible = fields.filter((f) => !f.hidden);
  const activeFields = visible.filter((f) => isFilterActiveValue(f.defaultValue, values[f.name], f.type));
  const searching = Boolean(search?.value.trim());

  // Quick filters: the page's own filters plus any field of the list, in the order the person chose (the page's list until then)
  const builtinMap = new Map(visible.map((f) => [f.name, f]));
  const defMap = new Map((fieldFilters?.fields ?? []).map((d) => [d.key, d]));
  const quick = useQuickFilters(quickKey, visible.map((f) => f.name));
  const entries = Array.from(new Set(quick.list))
    .filter((e) => (isFieldEntry(e) ? defMap.has(fieldKeyOf(e)) : builtinMap.has(e)))
    .slice(0, quick.max);
  const quickFieldKeys = new Set(entries.filter(isFieldEntry).map(fieldKeyOf));
  // Where each quick field's value sits among the added filters (those are shown by their pill, not listed again under Advanced)
  const firstIndex = new Map<string, number>();
  (fieldFilters?.value ?? []).forEach((f, i) => {
    if (quickFieldKeys.has(f.field) && !firstIndex.has(f.field)) firstIndex.set(f.field, i);
  });
  const pickerItems: PickerItem[] = [
    ...visible.map((f) => ({ key: f.name, label: f.label, group: "Standard filters", type: (f.type === "date" ? "date" : "enum") as PickerItem["type"] })),
    ...(fieldFilters?.fields ?? []).map((d) => ({ key: fieldEntry(d.key), label: d.label, group: d.group, type: d.type })),
  ];
  const canEditQuick = quick.editable && !!fieldFilters && fieldFilters.fields.length > 0;
  const saveQuick = (next: string[]) => {
    // A standard filter taken off the row must not keep filtering unseen
    entries.filter((e) => !isFieldEntry(e) && !next.includes(e)).forEach((e) => onChange(e, builtinMap.get(e)!.defaultValue));
    quick.save(next);
  };

  const [openName, setOpenName] = useState<string | null>(null);
  const [colsOpen, setColsOpen] = useState(false);
  // The filter row under the search box shows when the Filter button is on (and starts on if a filter is already set)
  const [filtersShown, setFiltersShown] = useState(() => activeFields.length + (fieldFilters?.value.length ?? 0) > 0);
  const [childSearch, setChildSearch] = useState("");
  const barRef = useRef<HTMLDivElement>(null);

  // Close any open pill or the Columns menu on an outside click or Escape
  useEffect(() => {
    if (!openName && !colsOpen) return;
    const close = () => {
      setOpenName(null);
      setColsOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openName, colsOpen]);

  const valueLabel = (f: FilterField, v: string) =>
    f.type === "date"
      ? dateFilterLabel(v, f.dates === "future" ? FUTURE_DATE_PRESETS : PAST_DATE_PRESETS)
      : f.options?.find((o) => o.value === v)?.label || v;

  const current = openName ? visible.find((f) => f.name === openName) || null : null;
  const filterCount = activeFields.length + (fieldFilters?.value.length ?? 0);
  // A filter switched on from elsewhere (a saved view, a link) opens the row, so it is never set but out of sight
  useEffect(() => {
    if (filterCount > 0) setFiltersShown(true);
  }, [filterCount]);
  const hasColumnChoice = columns && columns.options.some((c) => !c.locked);

  return (
    <div ref={barRef} className={cn("flex flex-col gap-2", className)}>
      <div className="flex flex-wrap items-center gap-2">
        {search && (
          <div className="relative w-full shrink-0 sm:w-64">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 z-10 size-3.5 -translate-y-1/2 select-none text-muted-foreground" />
            <Input
              data-list-search
              placeholder={search.placeholder}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search.onEnter?.()}
              className="h-8 rounded-lg border-border bg-panel pl-8 pr-7 text-xs"
            />
            {searching && (
              <button type="button" onClick={() => search.onChange("")} aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground">
                <X className="size-3.5" />
              </button>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => setFiltersShown((v) => !v)}
          aria-expanded={filtersShown}
          className={cn(
            "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border bg-background px-3 text-xs font-medium transition-colors hover:bg-muted",
            filterCount > 0 || filtersShown ? "border-primary/40 bg-accent text-accent-foreground" : "border-border",
          )}
        >
          <SlidersHorizontal className="size-3.5" />
          Filter{filterCount > 0 ? ` (${filterCount})` : ""}
        </button>
        {sort && <SortMenu fields={sort.fields} value={sort.value} onChange={sort.onChange} defaultLabel={sort.defaultLabel} />}
        {children && <div className="flex items-center gap-2">{children}</div>}

        <div className="flex items-center gap-3 sm:ml-auto">
          {shown !== undefined && (
            <span className="text-xs text-muted-foreground">
              Showing <strong className="text-foreground">{shown}</strong>
              {total !== undefined && total !== shown && (
                <>
                  {" "}
                  of <strong className="text-foreground">{total}</strong>
                </>
              )}{" "}
              {noun}
            </span>
          )}
          {columns && hasColumnChoice && (
            <div className="relative">
              <button
                type="button"
                onClick={() => { setColsOpen((o) => !o); setOpenName(null); }}
                aria-expanded={colsOpen}
                className={cn("inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs hover:bg-muted", colsOpen && "ring-2 ring-primary/30")}
              >
                <Columns3 className="size-3.5" />
                Columns
              </button>
              {colsOpen && (
                <div role="menu" className="absolute right-0 top-full z-50 mt-1.5 w-52 rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-xl animate-in fade-in-50 zoom-in-95 duration-100">
                  {columns.options.map((c) => {
                    const on = c.locked || columns.value.includes(c.key);
                    return (
                      <label key={c.key} className={cn("flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs", c.locked ? "cursor-default opacity-60" : "cursor-pointer hover:bg-muted")}>
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={c.locked}
                          onChange={() => columns.onChange(on ? columns.value.filter((k) => k !== c.key) : [...columns.value, c.key])}
                          className="size-3.5 accent-primary"
                        />
                        {c.label}
                      </label>
                    );
                  })}
                  <div className="mt-1 border-t border-border/60 px-1 pt-1">
                    <button type="button" onClick={() => columns.onChange(columns.options.map((c) => c.key))} className="w-full cursor-pointer rounded-lg px-2 py-1.5 text-left text-xs text-primary hover:bg-muted">
                      Show all
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {filtersShown && (visible.length > 0 || (fieldFilters && fieldFilters.fields.length > 0)) && (
        <div className="flex flex-wrap items-center gap-2">
        {entries.map((entry) => {
          if (isFieldEntry(entry)) {
            const def = defMap.get(fieldKeyOf(entry))!;
            const at = firstIndex.get(def.key);
            return (
              <QuickFieldPill
                key={entry}
                def={def}
                filter={at === undefined ? undefined : fieldFilters!.value[at]}
                suggestUrl={fieldFilters!.suggestUrl}
                onApply={(f) => fieldFilters!.onChange([...fieldFilters!.value.filter((x) => x.field !== def.key), f])}
                onClear={() => fieldFilters!.onChange(fieldFilters!.value.filter((x) => x.field !== def.key))}
              />
            );
          }
          const f = builtinMap.get(entry)!;
          const active = isFilterActiveValue(f.defaultValue, values[f.name], f.type);
          const isOpen = openName === f.name;
          return (
            <div key={f.name} className="relative">
              {active ? (
                <span className={cn("inline-flex h-7 items-center gap-1 rounded-md bg-accent pl-2.5 pr-1 text-xs font-medium text-accent-foreground", isOpen && "ring-2 ring-primary/40")}>
                  <button type="button" onClick={() => { setOpenName(isOpen ? null : f.name); setChildSearch(""); }} aria-expanded={isOpen} className="inline-flex max-w-[220px] cursor-pointer items-center gap-1">
                    <span className="opacity-70">{f.label}:</span>
                    <span className="truncate">{valueLabel(f, values[f.name]!)}</span>
                  </button>
                  <button type="button" onClick={() => onChange(f.name, f.defaultValue)} aria-label={`Remove ${f.label} filter`} className="flex size-5 cursor-pointer items-center justify-center rounded hover:bg-primary/15">
                    <X className="size-3" />
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => { setOpenName(isOpen ? null : f.name); setChildSearch(""); }}
                  aria-expanded={isOpen}
                  className={cn("inline-flex h-7 cursor-pointer items-center rounded-md border border-dashed border-border bg-background px-2.5 text-xs text-foreground/80 transition-colors hover:bg-muted", isOpen && "border-primary/50 ring-2 ring-primary/30")}
                >
                  {f.label}: {f.type === "date" ? "any time" : "any"}
                </button>
              )}
              {isOpen && current && (
                <div className="z-50 flex max-h-[380px] w-64 flex-col overflow-hidden rounded-xl border border-border bg-popover py-1 text-popover-foreground shadow-xl animate-in fade-in-50 zoom-in-95 duration-100 max-sm:fixed max-sm:inset-x-4 max-sm:bottom-4 max-sm:w-auto sm:absolute sm:left-0 sm:top-full sm:mt-1.5">
                  <ChildOptionsPanel
                    field={current}
                    currentValue={values[current.name] ?? current.defaultValue}
                    childSearch={childSearch}
                    setChildSearch={setChildSearch}
                    onChange={(val) => {
                      onChange(current.name, val);
                      setOpenName(null);
                    }}
                    onBack={() => setOpenName(null)}
                  />
                </div>
              )}
            </div>
          );
        })}

        {canEditQuick && (
          <>
            <span aria-hidden="true" className="mx-0.5 h-5 w-px bg-border" />
            <AddQuickFilter items={pickerItems} used={new Set(entries.map((e) => e))} disabled={entries.length >= quick.max} onAdd={(entry) => quick.save([...entries, entry])} />
            <EditQuickFilters items={pickerItems} list={entries} defaults={visible.map((f) => f.name)} max={quick.max} onSave={saveQuick} />
          </>
        )}

        {fieldFilters && fieldFilters.fields.length > 0 && (
          <FieldFilterChips
            fields={fieldFilters.fields}
            value={fieldFilters.value}
            onChange={fieldFilters.onChange}
            suggestUrl={fieldFilters.suggestUrl}
            hideIndexes={new Set(firstIndex.values())}
            addLabel="Advanced filters"
          />
        )}

        {(activeFields.length > 0 || searching || (fieldFilters?.value.length ?? 0) > 0) && (
          <button
            type="button"
            onClick={() => {
              onReset();
              search?.onChange("");
              fieldFilters?.onChange([]);
            }}
            className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-md px-2 text-xs font-medium text-primary hover:bg-muted"
          >
            <RotateCcw className="size-3" />
            Clear all
          </button>
        )}

        </div>
      )}
    </div>
  );
}

/** Side Child Options Panel for the active category */
function ChildOptionsPanel({
  field: f,
  currentValue,
  childSearch,
  setChildSearch,
  onChange,
  onBack,
}: {
  field: FilterField;
  currentValue: string;
  childSearch: string;
  setChildSearch: (s: string) => void;
  onChange: (v: string) => void;
  onBack: () => void;
}) {
  const isDate = f.type === "date";
  const presets = isDate
    ? f.dates === "future"
      ? FUTURE_DATE_PRESETS.filter((p) => p.value !== "custom")
      : PAST_DATE_PRESETS.filter((p) => p.value !== "custom")
    : f.options || [];

  const isFieldActive = isFilterActiveValue(f.defaultValue, currentValue, f.type);
  // Every filter list can be searched, like every dropdown
  const showSearch = !isDate;
  const filteredPresets = showSearch && childSearch.trim()
    ? presets.filter((p) => p.label.toLowerCase().includes(childSearch.toLowerCase()))
    : presets;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-2.5 py-1.5 border-b border-border/50 flex items-center justify-between gap-2 shrink-0 bg-muted/20">
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="sm:hidden text-muted-foreground hover:text-foreground p-0.5 rounded cursor-pointer"
            aria-label="Back to categories"
          >
            <ArrowLeft className="size-3.5" />
          </button>
          <span className="text-xs font-semibold text-foreground truncate">
            {f.label}
          </span>
        </div>

        {isFieldActive && (
          <button
            type="button"
            onClick={() => onChange(f.defaultValue)}
            className="text-[11px] text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          >
            Reset
          </button>
        )}
      </div>

      {/* Search within options */}
      {showSearch && (
        <div className="p-1.5 border-b border-border/40 shrink-0">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder={`Search ${f.label.toLowerCase()}...`}
              value={childSearch}
              onChange={(e) => setChildSearch(e.target.value)}
              className="w-full pl-6 pr-2 py-1 text-xs rounded bg-muted/40 border border-border/60 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      )}

      {/* Preset Options List */}
      <div className="overflow-y-auto p-1 space-y-0.5 flex-1 text-xs">
        {filteredPresets.length === 0 && (
          <div className="py-4 text-center text-xs text-muted-foreground">
            No options found
          </div>
        )}

        {filteredPresets.map((opt) => {
          const isSelected = currentValue === opt.value;

          return (
            <div
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={cn(
                "flex items-center justify-between px-2 py-1.5 rounded-lg cursor-pointer transition-colors gap-2",
                isSelected
                  ? "bg-primary/10 text-primary font-medium"
                  : "hover:bg-muted/70 text-foreground"
              )}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div
                  className={cn(
                    "size-3.5 rounded-full border flex items-center justify-center shrink-0 transition-colors",
                    isSelected
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-border/80"
                  )}
                >
                  {isSelected && <Check className="size-2 stroke-[3]" />}
                </div>
                <span className="truncate flex-1">{opt.label}</span>
              </div>

              {opt.count !== undefined && (
                <span className="text-[10px] text-muted-foreground tabular-nums px-1.5 py-0.2 rounded bg-muted/60 shrink-0">
                  {opt.count}
                </span>
              )}
            </div>
          );
        })}

        {/* Custom Date Range Component */}
        {isDate && (
          <CustomDateSection
            field={f}
            currentValue={currentValue}
            onApply={(from, to) => onChange(`custom:${from}:${to}`)}
            onClear={() => onChange(f.defaultValue)}
          />
        )}
      </div>
    </div>
  );
}

/** Custom Date Range input with validation and explicit Apply button */
function CustomDateSection({
  field,
  currentValue,
  onApply,
  onClear,
}: {
  field: FilterField;
  currentValue: string;
  onApply: (from: string, to: string) => void;
  onClear: () => void;
}) {
  const isCustomActive = currentValue.startsWith("custom");
  const [initFrom = "", initTo = ""] = isCustomActive ? currentValue.split(":").slice(1) : [];

  const [from, setFrom] = useState(initFrom);
  const [to, setTo] = useState(initTo);

  useEffect(() => {
    if (currentValue.startsWith("custom")) {
      const parts = currentValue.split(":");
      setFrom(parts[1] || "");
      setTo(parts[2] || "");
    } else {
      setFrom("");
      setTo("");
    }
  }, [currentValue]);

  const hasAnyDate = Boolean(from.trim() || to.trim());

  const handleApply = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!hasAnyDate) return;
    onApply(from.trim(), to.trim());
  };

  return (
    <div className="mt-1 pt-1.5 border-t border-border/50">
      <div className="p-2 rounded-lg bg-muted/40 border border-border/60 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
            <Calendar className="size-3 text-primary" />
            Custom Range
          </span>
          {isCustomActive && (
            <button
              type="button"
              onClick={onClear}
              className="text-[10px] text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
            >
              Clear
            </button>
          )}
        </div>

        <form onSubmit={handleApply} className="space-y-2">
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            <div className="space-y-0.5">
              <label className="text-[10px] text-muted-foreground font-medium">From</label>
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full h-7 px-1.5 text-[11px] rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="space-y-0.5">
              <label className="text-[10px] text-muted-foreground font-medium">To</label>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                className="w-full h-7 px-1.5 text-[11px] rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={!hasAnyDate}
            className={cn(
              "w-full h-7 text-xs font-medium rounded transition-colors cursor-pointer shadow-2xs flex items-center justify-center",
              hasAnyDate
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed opacity-60"
            )}
          >
            Apply Range
          </button>
        </form>
      </div>
    </div>
  );
}

export default FilterBar;
